import dynamic from 'next/dynamic'
import { useTranslation } from 'next-i18next'
import { type FC, useRef } from 'react'
import { useTheme } from 'styled-components'

import { TRANSLATION_KEYS } from '@/constants/localization'
import { MODAL_REGISTRY } from '@/features/modal/constants/modalRegistry'
import { SubCategories } from '@/features/navigation/components/mega-menu/desktop/SubCategories'
import {
  MegaMenuModalHeader,
  MegaMenuModalHeaderBackButton,
  MegaMenuModalPanel,
  SubCategoryMenu,
} from '@/features/navigation/components/styled'
import { type ActiveMegaMenuReturn } from '@/features/navigation/hooks/useActiveMegaMenu'
import { useClickOutside } from '@/hooks/useClickOutside'
import { HeadlineText } from '@/ui/atoms/headline-text'
import { Spacing } from '@/ui/atoms/spacing'
import { Modal } from '@/ui/molecules/modal/Modal'

const RightArrow = dynamic(() => import('@kijiji/icons/src/icons/Chevron'), { ssr: false })

type MegaMenuModalProps = Pick<
  ActiveMegaMenuReturn,
  | 'activeL2Items'
  | 'activeL3Items'
  | 'brandedCategoryCampaign'
  | 'closeMenu'
  | 'isCollapsedMenuView'
  | 'isKeyboardNavigation'
  | 'openMenu'
  | 'selectedL1Item'
  | 'selectedL2Item'
>

export const MegaMenuModal: FC<MegaMenuModalProps> = ({
  activeL2Items,
  activeL3Items,
  brandedCategoryCampaign,
  closeMenu,
  isCollapsedMenuView,
  isKeyboardNavigation,
  openMenu,
  selectedL1Item,
  selectedL2Item,
}) => {
  const { t } = useTranslation([TRANSLATION_KEYS.COMMON, TRANSLATION_KEYS.GLOBAL_HEADER])
  const { colors, spacing } = useTheme()
  const modalRef = useRef(null)

  const handleCloseModal = () => {
    closeMenu(2)
  }

  const handleCloseL3 = () => {
    closeMenu(3)
  }

  useClickOutside(modalRef, handleCloseModal)

  const isL3Menu = !!selectedL2Item && !!activeL3Items?.length

  return (
    <Modal
      id={MODAL_REGISTRY.MEGA_MENU}
      isOpen={isCollapsedMenuView && !!selectedL1Item && !!activeL2Items.length}
      label={t('mega_menu.nav.label')}
      onCancel={handleCloseModal}
      hasCloseButton
      closeButtonLabel={t('common:modals.close.button.label')}
      height={{ small: '100vh', mediumSmall: 'fit-content' }}
      maxHeight={{ small: '100vh', mediumSmall: '95vh' }}
      width={{ small: '100%', mediumSmall: '35rem', medium: '55rem' }}
      margin={{ small: '0', mediumSmall: '2vh auto' }}
      padding={`${!isL3Menu ? `${spacing.default} ${spacing.default}` : '0'}`}
    >
      <MegaMenuModalPanel ref={modalRef}>
        {!!selectedL1Item && !isL3Menu ? (
          <>
            <Spacing mTop={spacing.default} mBottom="1rem">
              <HeadlineText as="h3" size="large" color={colors.purple.primary}>
                {selectedL1Item?.categoryName}
              </HeadlineText>
            </Spacing>

            <SubCategoryMenu
              aria-labelledby={`cat-menu-item-${selectedL1Item?.id}`}
              id={`cat-menu-group-${selectedL1Item?.id}`}
              role="menu"
            >
              <SubCategories
                activeL2Items={activeL2Items}
                activeL3Items={activeL3Items}
                brandedCategoryCampaign={brandedCategoryCampaign}
                categoryLevel={2}
                isCollapsedMenuView
                isKeyboardNavigation={isKeyboardNavigation}
                openMenu={openMenu}
                parentCategoryId={selectedL1Item.id}
                parentCategoryName={selectedL1Item.categoryName}
                parentSeoUrl={selectedL1Item.seoUrl}
                selectedL1Item={selectedL1Item}
                closeMenu={closeMenu}
              />
            </SubCategoryMenu>
          </>
        ) : null}

        {isL3Menu ? (
          <>
            <MegaMenuModalHeader>
              <MegaMenuModalHeaderBackButton onClick={handleCloseL3}>
                <span>
                  <RightArrow aria-hidden />
                </span>
                {t('global_header:mega_menu.modal.back_to', {
                  categoryName: selectedL1Item?.categoryName,
                })}
              </MegaMenuModalHeaderBackButton>
            </MegaMenuModalHeader>

            <Spacing p={spacing.default}>
              <Spacing mTop={spacing.default} mBottom="1rem">
                <HeadlineText as="h3" size="large" color={colors.purple.primary}>
                  {selectedL2Item.categoryName}
                </HeadlineText>
              </Spacing>

              <SubCategoryMenu
                aria-labelledby={`cat-menu-item-${selectedL2Item.id}`}
                id={`cat-menu-group-${selectedL2Item.id}`}
                role="menu"
              >
                <SubCategories
                  activeL2Items={activeL2Items}
                  activeL3Items={activeL3Items}
                  brandedCategoryCampaign={brandedCategoryCampaign}
                  categoryLevel={3}
                  isCollapsedMenuView
                  isKeyboardNavigation={isKeyboardNavigation}
                  openMenu={openMenu}
                  parentCategoryId={selectedL2Item.id}
                  parentCategoryName={selectedL2Item.categoryName}
                  parentSeoUrl={selectedL2Item.seoUrl}
                  selectedL1Item={selectedL1Item}
                  closeMenu={closeMenu}
                />
              </SubCategoryMenu>
            </Spacing>
          </>
        ) : null}
      </MegaMenuModalPanel>
    </Modal>
  )
}
